import React from 'react';
import { CheckIcon } from '@heroicons/react/24/solid';

interface Step {
  id: string;
  name: string;
  description?: string;
}

interface WizardStepsProps {
  steps: Step[];
  currentStep: number;
  onStepClick?: (stepIndex: number) => void;
}

export default function WizardSteps({ steps, currentStep, onStepClick }: WizardStepsProps) {
  return (
    <nav aria-label="Progress" className="mb-8">
      <ol role="list" className="flex items-center justify-between">
        {steps.map((step, stepIdx) => (
          <li
            key={step.id}
            className={`relative ${stepIdx !== steps.length - 1 ? 'flex-1' : ''}`}
          >
            {stepIdx !== steps.length - 1 && (
              <div
                className="absolute top-4 w-full"
                aria-hidden="true"
              >
                <div className="h-0.5 w-full bg-gray-200">
                  <div
                    className="h-0.5 bg-amber-500 transition-all duration-500"
                    style={{
                      width: stepIdx < currentStep ? '100%' : '0%'
                    }}
                  />
                </div>
              </div>
            )}

            <button
              onClick={() => onStepClick && stepIdx < currentStep && onStepClick(stepIdx)}
              disabled={!onStepClick || stepIdx >= currentStep}
              className={`group relative flex items-center justify-center ${
                onStepClick && stepIdx < currentStep ? 'cursor-pointer' : 'cursor-default'
              }`}
            >
              <span className="flex h-8 items-center">
                {stepIdx < currentStep ? (
                  <span className="relative flex h-8 w-8 items-center justify-center rounded-full bg-amber-500 group-hover:bg-amber-600">
                    <CheckIcon className="h-5 w-5 text-white" aria-hidden="true" />
                  </span>
                ) : stepIdx === currentStep ? (
                  <span className="relative flex h-8 w-8 items-center justify-center rounded-full border-2 border-amber-500 bg-white">
                    <span className="h-2.5 w-2.5 rounded-full bg-amber-500" aria-hidden="true" />
                  </span>
                ) : (
                  <span className="relative flex h-8 w-8 items-center justify-center rounded-full border-2 border-gray-300 bg-white group-hover:border-gray-400">
                    <span className="h-2.5 w-2.5 rounded-full bg-transparent" aria-hidden="true" />
                  </span>
                )}
              </span>
              
              <span className="ml-3 text-sm font-medium">
                <span
                  className={`hidden sm:inline ${
                    stepIdx < currentStep
                      ? 'text-gray-900'
                      : stepIdx === currentStep
                      ? 'text-amber-600'
                      : 'text-gray-500'
                  }`}
                >
                  {step.name}
                </span>
                <span
                  className={`sm:hidden ${
                    stepIdx < currentStep
                      ? 'text-gray-900'
                      : stepIdx === currentStep
                      ? 'text-amber-600'
                      : 'text-gray-500'
                  }`}
                >
                  Step {stepIdx + 1}
                </span>
              </span>
            </button>

            {/* Step number indicators for mobile */}
            <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 sm:hidden">
              <span className="text-xs text-gray-500">{stepIdx + 1}</span>
            </div>
          </li>
        ))}
      </ol>

      {/* Progress indicator for mobile */}
      <div className="mt-8 sm:hidden">
        <div className="bg-gray-200 rounded-full h-2">
          <div
            className="bg-amber-500 h-2 rounded-full transition-all duration-500"
            style={{
              width: `${((currentStep + 1) / steps.length) * 100}%`
            }}
          />
        </div>
        <p className="text-center text-sm text-gray-600 mt-2">
          Step {currentStep + 1} of {steps.length}
        </p>
      </div>
    </nav>
  );
}